import { useEffect, useState } from 'react';
import { Modal } from '../components/Modal.js';
import { api } from '../lib/api.js';
import { askConfirm } from '../lib/dialogs.js';

/**
 * Reusable org-config list manager (Settings mega-menu → EHR Configuration).
 * Backs the four foundational entities with real CRUD:
 *   Statuses / Stages          → /settings/{statuses,stages}  (name + position)
 *   Contact Types / Referral   → /lookups/{contact-types,referral-sources} (name)
 *   Programs                   → /lookups/programs (name; the Calendar mirrors it, calendar#34)
 * Pipeline kinds (statuses/stages) are orderable; lookups are a flat name list.
 * Admin/manager only (server-gated).
 */
export type ConfigEntity =
  'statuses' | 'stages' | 'contact-types' | 'referral-sources' | 'task-statuses' | 'programs';
type Item = { id: string; name: string; position?: number };

const isPipeline = (e: ConfigEntity) => e === 'statuses' || e === 'stages';
const basePath = (e: ConfigEntity) => (isPipeline(e) ? `/settings/${e}` : `/lookups/${e}`);

// Task status is a fixed enum in core (TaskStatus: open/in_progress/done/cancelled),
// not an org-configurable table — so this page is read-only until the schema
// makes it configurable.
const TASK_STATUSES: Item[] = [
  { id: 'open', name: 'Open' },
  { id: 'in_progress', name: 'In Progress' },
  { id: 'done', name: 'Done' },
  { id: 'cancelled', name: 'Cancelled' },
];

export function ConfigListModal({
  entity,
  title,
  onClose,
}: {
  entity: ConfigEntity;
  title: string;
  onClose: () => void;
}) {
  const pipeline = isPipeline(entity);
  const readOnly = entity === 'task-statuses';
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const load = () => {
    if (readOnly) {
      setItems(TASK_STATUSES);
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get<Item[]>(basePath(entity))
      .then((rows) => {
        const list = Array.isArray(rows) ? rows.slice() : [];
        if (pipeline) list.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        setItems(list);
        setErr(null);
      })
      .catch(() => setErr('Could not load — admin/manager only.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [entity]);

  const add = () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    const body: { name: string; position?: number } = { name };
    if (pipeline) body.position = items.length;
    api
      .post(basePath(entity), body)
      .then(() => {
        setNewName('');
        load();
      })
      .catch(() => setErr('Could not add that item.'))
      .finally(() => setBusy(false));
  };

  const saveRename = (id: string) => {
    const name = editName.trim();
    setEditId(null);
    const cur = items.find((i) => i.id === id);
    if (!name || !cur || name === cur.name) return;
    api
      .patch(`${basePath(entity)}/${id}`, { name })
      .then(load)
      .catch(() => setErr('Could not rename.'));
  };

  const remove = async (id: string, name: string) => {
    // A deleted program is cleared from its clients, and the Calendar's enrollments in it go too.
    const warn =
      entity === 'programs'
        ? ' Clients in this program will no longer have it, and the Calendar drops their enrollments in it.'
        : '';
    if (!(await askConfirm(`Delete “${name}”?${warn}`))) return;
    api
      .del(`${basePath(entity)}/${id}`)
      .then(load)
      .catch(() => setErr('Could not delete — it may be in use.'));
  };

  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    const [m] = next.splice(i, 1);
    next.splice(j, 0, m!);
    setItems(next);
    Promise.all(
      next.map((it, idx) =>
        it.position === idx
          ? Promise.resolve(null)
          : api.patch(`${basePath(entity)}/${it.id}`, { name: it.name, position: idx }),
      ),
    )
      .then(load)
      .catch(() => setErr('Could not reorder.'));
  };

  const singular = title.replace(/s$/, '');

  return (
    <Modal
      title={title}
      onClose={onClose}
      className="config-modal"
      actions={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      <p className="muted config-sub">
        {readOnly
          ? 'Task statuses are system-managed — the same set applies to every org.'
          : pipeline
            ? 'Add, rename, reorder or remove — order sets how they appear across the app.'
            : entity === 'programs'
              ? 'Add, rename or remove programs. The Calendar uses this list; color and level of care are set in the Calendar.'
              : 'Add, rename or remove these options.'}
      </p>

      {!readOnly && (
        <div className="config-add">
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder={`New ${singular.toLowerCase()}…`}
            aria-label={`New ${singular}`}
          />
          <button className="btn primary" onClick={add} disabled={busy || !newName.trim()}>
            Add
          </button>
        </div>
      )}

      {err && <div className="config-err">{err}</div>}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted config-empty">No {title.toLowerCase()} yet.</p>
      ) : (
        <div className="config-list">
          {items.map((it, i) => (
            <div className="config-row" key={it.id}>
              {pipeline && !readOnly && (
                <span className="config-arrows">
                  <button
                    type="button"
                    aria-label={`Move ${it.name} up`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${it.name} down`}
                    disabled={i === items.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    ↓
                  </button>
                </span>
              )}
              {readOnly ? (
                <span className="config-name config-name-static">{it.name}</span>
              ) : editId === it.id ? (
                <input
                  className="config-edit"
                  autoFocus
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => saveRename(it.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveRename(it.id);
                    if (e.key === 'Escape') setEditId(null);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="config-name"
                  onClick={() => {
                    setEditId(it.id);
                    setEditName(it.name);
                  }}
                  title="Click to rename"
                >
                  {it.name}
                </button>
              )}
              {!readOnly && (
                <button
                  type="button"
                  className="config-del"
                  aria-label={`Delete ${it.name}`}
                  onClick={() => remove(it.id, it.name)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
